import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

/** Partner revenue share (super_admin). Backend: al-user-management
 *  `referral_endpoints.py`, PRD `features/prd/referral-commissions.md`. */

export type VatMode = 'regular' | 'small_business' | 'reverse_charge';
export type CommissionStatus = 'open' | 'released' | 'paid';

export type BillingMode = 'self_billing' | 'partner_invoice';
export type PayoutMode = 'manual' | 'auto';

export interface ReferralPartner {
  id: string;
  name: string;
  email?: string | null;
  vat_mode: VatMode;
  active: boolean;
  note?: string | null;
  legal_name?: string | null;
  street?: string | null;
  zip?: string | null;
  city?: string | null;
  country: string;
  tax_id?: string | null;
  /** Printed on the Gutschrift, e.g. "Affiliate-Vereinbarung vom 15.09.2026". */
  contract_reference?: string | null;
  billing_mode: BillingMode;
  payout_mode: PayoutMode;
  payout_day: number;
  stripe_account_id?: string | null;
  /** Fields a Gutschrift still needs (empty = complete). */
  missing: string[];
  /** Who issues the Gutschrift (migration 084). */
  billing_via: 'alphalearn' | 'alphacount';
  /** Cutover day: revenue from here on is settled by alphacount. */
  alphacount_from?: string | null;
  /** Date of the self-billing agreement — alphacount issues no Gutschrift without it. */
  self_billing_agreed_on?: string | null;
  ac_synced_at?: string | null;
  /** Problems of the last mirror to alphacount, one per line. */
  ac_sync_error?: string | null;
}

export interface ConnectStatus {
  connected: boolean;
  account_id?: string;
  details_submitted: boolean;
  payouts_enabled: boolean;
  transfers_active: boolean;
  requirements_due: string[];
  disabled_reason?: string | null;
}

export interface PayoutPreview {
  rows: number;
  net_cents: number;
  vat_cents: number;
  gross_cents: number;
  missing: string[];
}

export interface ReferralStatement {
  id: string;
  number: string;
  kind: BillingMode;
  partner_name: string | null;
  issued_on: string;
  period_start: string | null;
  period_end: string | null;
  net_cents: number;
  vat_cents: number;
  gross_cents: number;
  status: 'issued' | 'awaiting_invoice' | 'paid' | 'failed' | 'cancelled';
  partner_invoice_ref: string | null;
  transfer_ids: string[];
  error: string | null;
  sent_at: string | null;
  paid_at: string | null;
  lines: number;
  /** SHA-256 of the archived PDF (the exact document issued and mailed). */
  pdf_sha256: string | null;
  pdf_created_at: string | null;
  mail_log: { at: string; to: string; from: string; message_id: string; trigger: string }[];
  /** `alphacount`: issued, mailed and archived there; we only pay it. */
  source?: 'alphalearn' | 'alphacount';
  ac_statement_id?: string | null;
  ac_payment_reported_at?: string | null;
  /** Automatic payout after the review window, unless held. */
  payout_due_at?: string | null;
  payout_held_at?: string | null;
  payout_held_by?: string | null;
}

export interface ReferralTier {
  from_month: number;
  to_month: number | null;
  percent: number;
}

export interface ReferralTotals {
  open: number;
  released: number;
  paid: number;
}

export interface ReferralAgreement {
  id: string;
  client_id: string;
  client_name: string | null;
  client_subscription_status: string | null;
  partner_id: string;
  partner_name: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  start_date: string;
  end_date: string | null;
  basis: 'net' | 'gross';
  tiers: ReferralTier[];
  note: string | null;
  current_month: number | null;
  /** null while the client is not paying or the referral has ended. */
  current_percent: number | null;
  running: boolean;
  totals: ReferralTotals;
}

export interface ReferralCommission {
  id: string;
  agreement_id: string;
  partner_name: string | null;
  client_name: string | null;
  source_type: 'invoice' | 'credit_note';
  invoice_number: string | null;
  stripe_invoice_id: string | null;
  paid_at: string | null;
  period_start: string | null;
  contract_month: number | null;
  base_cents: number;
  percent: number;
  commission_cents: number;
  vat_cents: number;
  gross_cents: number;
  currency: string;
  status: CommissionStatus;
  released_at: string | null;
  released_by: string | null;
  paid_out_at: string | null;
  paid_out_by: string | null;
  payout_reference: string | null;
}

/** A client that pays (Stripe subscription, paid plan) and has no running referral. */
export interface EligibleClient {
  id: string;
  name: string | null;
  email: string | null;
  plan: string;
  billing_interval: string;
  subscription_status: string;
}

export interface AgreementPayload {
  client_id?: string;
  partner_id?: string;
  start_date?: string;
  end_date?: string | null;
  clear_end_date?: boolean;
  tiers?: ReferralTier[];
  note?: string | null;
}

export interface CommissionFilter {
  partner_id?: string | null;
  status?: CommissionStatus | null;
}

@Injectable({ providedIn: 'root' })
export class ReferralService {
  private readonly base = `${environment.userBackend}/admin/referrals`;

  constructor(private http: HttpClient) {}

  listPartners() {
    return this.http.get<{ data: ReferralPartner[] }>(`${this.base}/partners`).pipe(map((r) => r.data));
  }

  createPartner(p: Partial<ReferralPartner>) {
    return this.http.post<{ data: ReferralPartner }>(`${this.base}/partners`, p).pipe(map((r) => r.data));
  }

  updatePartner(p: ReferralPartner) {
    return this.http.patch<{ data: ReferralPartner }>(`${this.base}/partners/${p.id}`, p).pipe(map((r) => r.data));
  }

  deletePartner(partnerId: string) {
    return this.http.delete(`${this.base}/partners/${partnerId}`);
  }

  connectStatus(partnerId: string) {
    return this.http.get<{ data: ConnectStatus }>(`${this.base}/partners/${partnerId}/connect`).pipe(map((r) => r.data));
  }

  /** Our long-lived onboarding link (14 days); `sendMail` mails it to the partner. */
  onboardingLink(partnerId: string, sendMail = false) {
    return this.http
      .post<{ data: { url: string; mailed: boolean; valid_days: number } }>(
        `${this.base}/partners/${partnerId}/connect/onboarding`,
        { send_mail: sendMail }
      )
      .pipe(map((r) => r.data));
  }

  preview(partnerId: string) {
    return this.http.get<{ data: PayoutPreview }>(`${this.base}/partners/${partnerId}/preview`).pipe(map((r) => r.data));
  }

  runPayout(partnerId: string) {
    return this.http
      .post<{ data: ReferralStatement }>(`${this.base}/partners/${partnerId}/run`, {})
      .pipe(map((r) => r.data));
  }

  listStatements() {
    return this.http.get<{ data: ReferralStatement[] }>(`${this.base}/statements`).pipe(map((r) => r.data));
  }

  statementPdf(id: string) {
    return this.http.get(`${this.base}/statements/${id}/pdf`, { responseType: 'blob' });
  }

  payStatement(id: string, partner_invoice_ref?: string) {
    return this.http
      .post<{ data: ReferralStatement }>(`${this.base}/statements/${id}/pay`, { partner_invoice_ref })
      .pipe(map((r) => r.data));
  }

  /** Hold the scheduled payout (review window) or release it again. */
  holdStatement(id: string, hold: boolean) {
    return this.http
      .post<{ data: ReferralStatement }>(`${this.base}/statements/${id}/hold`, { hold })
      .pipe(map((r) => r.data));
  }

  resendStatement(id: string) {
    return this.http.post<{ data: ReferralStatement }>(`${this.base}/statements/${id}/send`, {}).pipe(map((r) => r.data));
  }

  listEligibleClients() {
    return this.http.get<{ data: EligibleClient[] }>(`${this.base}/eligible-clients`).pipe(map((r) => r.data));
  }

  listAgreements() {
    return this.http.get<{ data: ReferralAgreement[] }>(`${this.base}/agreements`).pipe(map((r) => r.data));
  }

  createAgreement(body: AgreementPayload) {
    return this.http.post<{ data: ReferralAgreement; stripe_warning: string | null }>(`${this.base}/agreements`, body);
  }

  updateAgreement(id: string, body: AgreementPayload) {
    return this.http.patch<{ data: ReferralAgreement; stripe_warning: string | null }>(
      `${this.base}/agreements/${id}`,
      body
    );
  }

  syncAgreement(id: string) {
    return this.http.post<{ data: Record<string, number | string> }>(`${this.base}/agreements/${id}/sync`, {});
  }

  listCommissions(filter: CommissionFilter) {
    return this.http
      .get<{ data: ReferralCommission[] }>(`${this.base}/commissions`, { params: this.params(filter) })
      .pipe(map((r) => r.data));
  }

  release(ids: string[]) {
    return this.http.post(`${this.base}/commissions/release`, { ids });
  }

  unrelease(ids: string[]) {
    return this.http.post(`${this.base}/commissions/unrelease`, { ids });
  }

  markPaid(ids: string[], payout_reference: string) {
    return this.http.post(`${this.base}/commissions/mark-paid`, { ids, payout_reference });
  }

  exportCsv(filter: CommissionFilter) {
    return this.http.get(`${this.base}/commissions/export.csv`, {
      params: this.params(filter),
      responseType: 'blob',
    });
  }

  private params(filter: CommissionFilter): HttpParams {
    let p = new HttpParams();
    if (filter.partner_id) p = p.set('partner_id', filter.partner_id);
    if (filter.status) p = p.set('status', filter.status);
    return p;
  }
}
